import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Typography, Chip, useTheme, Stack } from "@mui/material";
import { GridText } from "src/components/Grid/GridText";
import { StatusTag } from "src/components/Actions/Common/StatusTag";
import { ActionGridData } from "./ActionsGrid";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { Loading } from "src/components/utils/Loading";
import { Suspense } from "src/components/utils/Suspense";
import { NumberWithUnit } from "@foodpilot/foods";
import { getScorePrecision } from "src/context/Scores/provider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { useMemo } from "react";

export type MrtColumnsAction = {
    mrt_columns: MRT_ColumnDef<ActionGridData, any>[];
};

export const useActionColumns = (): MrtColumnsAction => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isFetched, urls, scoresEnabled } = useActionsContext();
    const { formatScore, getColor, scoresWithVariations } = useEntityScores();

    const columnHelper = createMRTColumnHelper<ActionGridData>();
    const noValue = "-";

    const impactColumns = useMemo(() => {
        return scoresEnabled.map((score) => {
            const scoreUnit =
                scoresWithVariations.find((row) => row.scoreId === score.id)?.unit ?? score.unit?.abbreviation ?? "";
            const precision = getScorePrecision(score);

            return columnHelper.accessor((row) => row.impactsByScore[score.id] ?? null, {
                id: `impact-${score.id}`,
                header: score.title,
                Cell: (value) => {
                    const impact = value.cell.getValue() as number | null;

                    if (impact === null) {
                        return (
                            <div style={{ textAlign: "right" }}>
                                <GridText value={t("action_plan.action.non_quantifiable", "Not quantifiable")} />
                            </div>
                        );
                    }

                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                justifyContent: "flex-end",
                                width: "100%",
                            }}
                        >
                            <Suspense isLoading={!isFetched} fallback={<Loading size={20} sx={{ margin: 0 }} />}>
                                <ApproximateImpactTooltip value={impact} precision={precision} unit={scoreUnit}>
                                    <NumberWithUnit
                                        value={formatScore(impact, { precision })}
                                        unit={scoreUnit}
                                        size="small"
                                        colorOverride={getColor(impact, { improvement: score.improvement, precision })}
                                    />
                                </ApproximateImpactTooltip>
                            </Suspense>
                        </div>
                    );
                },
                size: 140,
                muiTableBodyCellProps: { align: "right" },
                muiTableHeadCellProps: { align: "right" },
            });
        });
    }, [scoresEnabled, scoresWithVariations, columnHelper, formatScore, getColor, getScorePrecision, isFetched, t]);

    const mrt_columns: MRT_ColumnDef<ActionGridData, any>[] = [
        columnHelper.accessor("order", {
            header: "#",
            Cell: (value) => <GridText value={value.cell.getValue()} />,
            size: 56,
            grow: false,
            enableColumnActions: false,
        }),

        columnHelper.accessor("title", {
            header: t("actions.actions", "Actions"),
            Cell: (value) => {
                const url = urls.editPlanning(value.row.original.id);

                return (
                    <Link
                        to={url}
                        style={{
                            textDecoration: "none",
                        }}
                    >
                        <Typography variant="cta" color={theme.palette.primary[600]}>
                            {value.renderedCellValue}
                        </Typography>
                    </Link>
                );
            },
            size: 300,
        }),

        columnHelper.accessor((row) => row.categories, {
            id: "categories",
            header: t("actions.category", "Category"),
            Cell: (value) => {
                const cellValue = value.cell.getValue();
                const categories =
                    cellValue.length > 0 ? cellValue : [t("action_plan.action.not_categorized", "Not categorized")];

                return (
                    <Stack direction="row" gap={theme.spacing(0.5)}>
                        {categories.map((category: { title: string }) => (
                            <Chip
                                key={category.title}
                                label={category.title}
                                sx={{
                                    backgroundColor: "#EFEDEC",
                                    color: "#50504D",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    minHeight: "26px",
                                    height: "auto",
                                    borderRadius: "4px",
                                    padding: "2px 8px",
                                    maxWidth: "100%",
                                    "& .MuiChip-label": {
                                        padding: 0,
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        lineHeight: "1.6",
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                        textAlign: "center",
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                );
            },
            sortingFn: (rowA, rowB) => {
                const categoriesA = rowA.original.categories;
                const categoriesB = rowB.original.categories;

                if (categoriesA.length === 1 && categoriesB.length === 1) {
                    return categoriesA[0].title.localeCompare(categoriesB[0].title);
                }

                if (categoriesA.length !== categoriesB.length) {
                    return categoriesA.length - categoriesB.length;
                }

                return 0;
            },
            size: 150,
        }),

        columnHelper.accessor("startYear", {
            header: t("actions.start_year", "Start year"),
            Cell: (value) => <GridText value={value.cell.getValue() || noValue} />,
            size: 100,
        }),

        columnHelper.accessor("endYear", {
            header: t("actions.end_year", "End year"),
            Cell: (value) => <GridText value={value.cell.getValue() || noValue} />,
            size: 100,
        }),

        columnHelper.accessor("status", {
            header: t("actions.status", "Status"),
            Cell: (value) => <StatusTag label={value.cell.getValue()} />,
            size: 120,
        }),

        ...impactColumns,
    ];

    return { mrt_columns };
};
